
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    Box, Card, Typography, TextField, Button, Switch,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Alert, CircularProgress, IconButton, Tooltip, Paper, Chip, Autocomplete,
    Dialog, DialogTitle, DialogContent, DialogActions, Divider
} from '@mui/material';
import {
    Save as SaveIcon, Refresh as RefreshIcon, Edit as EditIcon,
    Cancel as CancelIcon, Delete as DeleteIcon, PlayArrow as PlayArrowIcon
} from '@mui/icons-material';
import { API_BASE, CRON_SERVICE_URL, CRON_API_KEY } from '../config';

interface CronSetting {
    jobName: string;
    cronExpression: string;
    description: string;
    isActive: boolean;
}

interface UserOption {
    email: string;
    name: string;
}

// Helper function to convert cron to human-readable format
const cronToReadable = (cron: string): string => {
    const parts = cron.trim().split(' ');
    if (parts.length !== 5) return cron;
    const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;
    if (dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
        const h = parseInt(hour);
        const m = parseInt(minute);
        const period = h >= 12 ? 'PM' : 'AM';
        const displayHour = h > 12 ? h - 12 : (h === 0 ? 12 : h);
        return `Daily at ${displayHour}:${m.toString().padStart(2, '0')} ${period}`;
    }
    return cron;
};

const parseCronTime = (cron: string): { hour: number; minute: number } => {
    const parts = cron.trim().split(' ');
    if (parts.length >= 2) {
        return { minute: parseInt(parts[0]) || 0, hour: parseInt(parts[1]) || 0 };
    }
    return { hour: 0, minute: 0 };
};

const TargetedCron: React.FC = () => {
    // Email management state
    const [targetEmails, setTargetEmails] = useState<string[]>([]);
    const [searchOptions, setSearchOptions] = useState<UserOption[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchInput, setSearchInput] = useState('');

    // Rate override state
    const [normalRate, setNormalRate] = useState<string>('');
    const [platinumRate, setPlatinumRate] = useState<string>('');
    const [rateLoading, setRateLoading] = useState(false);

    // Cron schedule state
    const [cronSettings, setCronSettings] = useState<CronSetting[]>([]);
    const [cronSchedules, setCronSchedules] = useState<Record<string, string>>({});

    // Edit state
    const [editing, setEditing] = useState<string | null>(null);
    const [tempHour, setTempHour] = useState(12);
    const [tempMinute, setTempMinute] = useState(0);
    const [tempPeriod, setTempPeriod] = useState<'AM' | 'PM'>('AM');

    // UI state
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const [manualDate, setManualDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [runLoading, setRunLoading] = useState<string | null>(null);
    const [saveLoading, setSaveLoading] = useState(false);
    const [hasPendingChanges, setHasPendingChanges] = useState(false);
    const [confirmClear, setConfirmClear] = useState(false);

    // Service health
    const [serviceHealth, setServiceHealth] = useState<'UP' | 'DOWN' | 'DEGRADED' | 'CHECKING' | 'IDLE' | 'RESTARTING'>('CHECKING');
    const [cooldownSeconds, setCooldownSeconds] = useState(0);

    const targetedJobs = ['targeted-normal-personal', 'targeted-platinum-personal'];

    const getToken = () => localStorage.getItem('token');

    // Fetch stored email targets + rate/date config
    const fetchEmailTargets = useCallback(async () => {
        try {
            const res = await axios.get(`${API_BASE}/api/cron-email-targets`, {
                headers: { Authorization: `Bearer ${getToken()}` }
            });
            if (res.data.status === 'success') {
                const d = res.data.data;
                setTargetEmails(d?.emails || []);
                setNormalRate(d?.normalRate != null ? String(d.normalRate) : '');
                setPlatinumRate(d?.platinumRate != null ? String(d.platinumRate) : '');
            }
        } catch (err: any) {
            console.error('Failed to fetch email targets:', err);
        }
    }, []);

    // Fetch cron settings for targeted jobs
    const fetchCronSettings = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [rulesRes, cronRes] = await Promise.all([
                axios.get(`${API_BASE}/api/business-rules`, { headers: { Authorization: `Bearer ${getToken()}` } }),
                axios.get(`${API_BASE}/api/cron-settings`, { headers: { Authorization: `Bearer ${getToken()}` } })
            ]);

            if (rulesRes.data.status === 'success') {
                const cronRule = rulesRes.data.data.find((r: any) => r.key === 'CRON_SCHEDULES');
                if (cronRule) {
                    const schedules: Record<string, string> = {};
                    for (const job of targetedJobs) {
                        if (cronRule.value[job]) {
                            schedules[job] = cronRule.value[job];
                        }
                    }
                    setCronSchedules(schedules);
                }
            }

            if (cronRes.data.status === 'success') {
                setCronSettings(cronRes.data.data.filter((s: CronSetting) => targetedJobs.includes(s.jobName)));
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to fetch settings');
        } finally {
            setLoading(false);
        }
    }, []);

    // Search users by email/name
    const searchUsers = useCallback(async (query: string) => {
        if (query.length < 2) {
            setSearchOptions([]);
            return;
        }
        setSearchLoading(true);
        try {
            const res = await axios.get(`${API_BASE}/api/user/getUsers/all`, {
                params: { search: query, limit: 10, page: 1 },
                headers: { Authorization: `Bearer ${getToken()}` }
            });
            const users = res.data.data?.users || res.data.data || [];
            setSearchOptions(users.map((u: any) => ({ email: u.email, name: u.name || u.email })));
        } catch (err) {
            setSearchOptions([]);
        } finally {
            setSearchLoading(false);
        }
    }, []);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchInput.length >= 2) searchUsers(searchInput);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchInput, searchUsers]);

    // Health check
    const checkServiceHealth = useCallback(async () => {
        try {
            const res = await axios.get(`${CRON_SERVICE_URL}/health`, { timeout: 5000 });
            if (res.data.status === 'UP') setServiceHealth('UP');
            else if (res.data.status === 'DEGRADED') setServiceHealth('DEGRADED');
            else if (res.data.status === 'IDLE') setServiceHealth('IDLE');
            else setServiceHealth('DOWN');
        } catch {
            setServiceHealth(prev => prev === 'RESTARTING' ? 'RESTARTING' : 'DOWN');
        }
    }, []);

    useEffect(() => {
        fetchEmailTargets();
        fetchCronSettings();
        checkServiceHealth();
        const healthInterval = setInterval(checkServiceHealth, 10000);
        return () => clearInterval(healthInterval);
    }, [fetchEmailTargets, fetchCronSettings, checkServiceHealth]);

    useEffect(() => {
        if (cooldownSeconds > 0) {
            const timer = setTimeout(() => setCooldownSeconds(cooldownSeconds - 1), 1000);
            return () => clearTimeout(timer);
        } else {
            setServiceHealth(prev => prev === 'RESTARTING' ? 'DOWN' : prev);
        }
    }, [cooldownSeconds]);

    // Add email to list
    const handleAddEmail = (email: string) => {
        const normalized = email.trim().toLowerCase();
        if (normalized && !targetEmails.includes(normalized)) {
            setTargetEmails(prev => [...prev, normalized]);
        }
    };

    // Remove email from list
    const handleRemoveEmail = (email: string) => {
        setTargetEmails(prev => prev.filter(e => e !== email));
    };

    // Save emails to backend
    const handleSaveEmails = async () => {
        setSaveLoading(true);
        try {
            const res = await axios.put(`${API_BASE}/api/cron-email-targets`,
                { emails: targetEmails },
                { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            if (res.data.status === 'success') {
                setSuccess('Target emails saved successfully');
                setTimeout(() => setSuccess(null), 3000);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to save emails');
        } finally {
            setSaveLoading(false);
        }
    };

    // Save interest rates
    const handleSaveRates = async () => {
        setRateLoading(true);
        try {
            const res = await axios.put(`${API_BASE}/api/cron-email-targets`,
                {
                    normalRate: normalRate !== '' ? Number(normalRate) : null,
                    platinumRate: platinumRate !== '' ? Number(platinumRate) : null,
                },
                { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            if (res.data.status === 'success') {
                setSuccess('Interest rates saved successfully');
                setTimeout(() => setSuccess(null), 3000);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to save rates');
        } finally {
            setRateLoading(false);
        }
    };

    // Clear all emails
    const handleClearEmails = async () => {
        try {
            await axios.delete(`${API_BASE}/api/cron-email-targets`, {
                headers: { Authorization: `Bearer ${getToken()}` }
            });
            setTargetEmails([]);
            setSuccess('All target emails cleared');
            setConfirmClear(false);
            setTimeout(() => setSuccess(null), 3000);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to clear emails');
        }
    };

    // Toggle job active/inactive
    const handleToggleActive = async (jobName: string, currentStatus: boolean) => {
        setActionLoading(`toggle-${jobName}`);
        try {
            const res = await axios.put(`${API_BASE}/api/cron-settings/${jobName}`,
                { isActive: !currentStatus },
                { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            if (res.data.status === 'success') {
                setCronSettings(prev => prev.map(s => s.jobName === jobName ? { ...s, isActive: !currentStatus } : s));
                setSuccess(`${jobName} is now ${!currentStatus ? 'Active' : 'Deactivated'}`);
                setTimeout(() => setSuccess(null), 3000);
                setHasPendingChanges(true);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Toggle failed');
        } finally {
            setActionLoading(null);
        }
    };

    // Update cron schedule
    const handleUpdateCronSchedule = async (jobKey: string) => {
        try {
            let hour24 = tempHour;
            if (tempPeriod === 'PM' && tempHour !== 12) hour24 = tempHour + 12;
            else if (tempPeriod === 'AM' && tempHour === 12) hour24 = 0;

            const cronExpression = `${tempMinute} ${hour24} * * *`;

            // Fetch current full CRON_SCHEDULES, then merge the update
            const rulesRes = await axios.get(`${API_BASE}/api/business-rules`, {
                headers: { Authorization: `Bearer ${getToken()}` }
            });
            let allSchedules: Record<string, string> = {};
            if (rulesRes.data.status === 'success') {
                const cronRule = rulesRes.data.data.find((r: any) => r.key === 'CRON_SCHEDULES');
                if (cronRule) allSchedules = { ...cronRule.value };
            }
            allSchedules[jobKey] = cronExpression;

            const res = await axios.put(`${API_BASE}/api/business-rules/CRON_SCHEDULES`,
                { value: allSchedules },
                { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            if (res.data.status === 'success') {
                setCronSchedules(prev => ({ ...prev, [jobKey]: cronExpression }));
                setSuccess(`Updated ${jobKey} schedule successfully`);
                setEditing(null);
                setTimeout(() => setSuccess(null), 3000);
                setHasPendingChanges(true);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Update failed');
        }
    };

    // Manual run for targeted emails
    const handleManualRun = async (jobName: string) => {
        if (targetEmails.length === 0) {
            setError('No target emails configured. Add emails first.');
            return;
        }
        if (!manualDate) {
            setError('Please select a date for manual run');
            return;
        }

        const cronJobType = jobName.replace('targeted-', '');
        const displayName = cronJobType === 'normal-personal' ? 'Normal Self Income' : 'Platinum Self Income';

        if (!window.confirm(`Run ${displayName} for ${targetEmails.length} targeted email(s) on ${manualDate}?`)) return;

        setRunLoading(jobName);
        try {
            const res = await axios.post(`${API_BASE}/api/cron-email-targets/trigger/${jobName}`,
                {
                    date: manualDate,
                    normalRate: normalRate !== '' ? Number(normalRate) : undefined,
                    platinumRate: platinumRate !== '' ? Number(platinumRate) : undefined,
                },
                { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            if (res.data.status === 'success') {
                setSuccess(`Started ${displayName} for ${targetEmails.length} targeted email(s) on ${manualDate}`);
                setTimeout(() => setSuccess(null), 5000);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Manual run failed');
        } finally {
            setRunLoading(null);
        }
    };

    // Restart cron service
    const restartCronService = async () => {
        setActionLoading('restart');
        try {
            const res = await axios.post(`${CRON_SERVICE_URL}/admin/system/restart`, {}, {
                headers: { 'X-Api-Key': CRON_API_KEY }
            });
            if (res.data.status === 'success') {
                setSuccess('System restart initiated. Please wait 15-20 seconds.');
                setCooldownSeconds(20);
                setServiceHealth('RESTARTING');
                setHasPendingChanges(false);
                setTimeout(() => checkServiceHealth(), 10000);
            }
        } catch (err: any) {
            if (err.response?.status === 401) {
                setError('Authentication failed: Check REACT_APP_CRON_API_KEY in your .env file.');
            } else {
                setError('Failed to restart: ' + (err.response?.data?.message || err.message));
            }
        } finally {
            setActionLoading(null);
        }
    };

    const getJobDisplayName = (jobName: string): string => {
        if (jobName === 'targeted-normal-personal') return 'Normal Self Income (Targeted)';
        if (jobName === 'targeted-platinum-personal') return 'Platinum Self Income (Targeted)';
        return jobName;
    };

    return (
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            {/* Header */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mb: 3, gap: 2 }}>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, mb: 1, flexWrap: 'wrap' }}>
                        <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>Targeted Cron</Typography>
                        <Paper
                            elevation={0}
                            sx={{
                                px: 1.5, py: 0.5,
                                bgcolor: serviceHealth === 'UP' ? 'success.50' : serviceHealth === 'DOWN' ? 'error.50' : serviceHealth === 'RESTARTING' ? 'warning.100' : 'grey.100',
                                border: '1px solid',
                                borderColor: serviceHealth === 'UP' ? 'success.main' : serviceHealth === 'DOWN' ? 'error.main' : serviceHealth === 'RESTARTING' ? 'warning.dark' : 'grey.400',
                                borderRadius: 2, display: 'flex', alignItems: 'center', gap: 0.5
                            }}
                        >
                            <Box sx={{
                                width: 8, height: 8, borderRadius: '50%',
                                bgcolor: serviceHealth === 'UP' ? 'success.main' : serviceHealth === 'DOWN' ? 'error.main' : serviceHealth === 'RESTARTING' ? 'warning.main' : 'grey.500',
                                animation: (serviceHealth === 'CHECKING' || serviceHealth === 'RESTARTING') ? 'pulse 1.5s ease-in-out infinite' : 'none',
                                '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } }
                            }} />
                            <Typography variant="caption" fontWeight={600}
                                color={serviceHealth === 'UP' ? 'success.dark' : serviceHealth === 'DOWN' ? 'error.dark' : serviceHealth === 'RESTARTING' ? 'warning.dark' : 'text.secondary'}
                            >
                                Cron Service: {serviceHealth === 'UP' ? 'Running' : serviceHealth === 'DOWN' ? 'Stopped' : serviceHealth === 'RESTARTING' ? 'Restarting...' : serviceHealth === 'IDLE' ? 'Idle' : 'Checking...'}
                            </Typography>
                        </Paper>
                    </Box>
                    <Typography variant="body1" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                        Run income cron jobs for specific targeted email addresses
                    </Typography>
                </Box>
                <Button startIcon={<RefreshIcon />} onClick={() => { fetchEmailTargets(); fetchCronSettings(); }} variant="contained" disabled={loading} size="small" sx={{ borderRadius: 2 }}>
                    Refresh
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>{success}</Alert>}

            {hasPendingChanges && (
                <Alert severity="warning" sx={{ mb: 3 }}
                    action={
                        <Button color="warning" variant="contained" size="small"
                            onClick={restartCronService}
                            disabled={!!actionLoading || cooldownSeconds > 0}
                        >
                            {actionLoading === 'restart' ? <CircularProgress size={20} color="inherit" /> : 'Apply Changes & Restart'}
                        </Button>
                    }
                >
                    <Typography variant="body2" fontWeight={600}>
                        You have unapplied schedule changes. Click 'Apply Changes & Restart' to make them active.
                    </Typography>
                </Alert>
            )}

            {/* Section 1: Email Management */}
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: { xs: 2, sm: 3 }, mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight={700}>Target Emails</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip label={`${targetEmails.length} email(s)`} size="small" color="primary" variant="outlined" />
                    </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Search and add users whose income will be processed by the targeted cron jobs below. This email list is shared by both Normal and Platinum self income jobs.
                </Typography>

                {/* Search and Add */}
                <Autocomplete
                    freeSolo
                    options={searchOptions}
                    getOptionLabel={(option) => typeof option === 'string' ? option : `${option.name} (${option.email})`}
                    loading={searchLoading}
                    inputValue={searchInput}
                    onInputChange={(_, value) => setSearchInput(value)}
                    onChange={(_, value) => {
                        if (value) {
                            const email = typeof value === 'string' ? value : value.email;
                            handleAddEmail(email);
                            setSearchInput('');
                        }
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && searchInput.trim()) {
                            e.preventDefault();
                            handleAddEmail(searchInput.trim());
                            setSearchInput('');
                        }
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Search users by name or email"
                            placeholder="Type to search..."
                            size="small"
                            sx={{ mb: 2 }}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {searchLoading ? <CircularProgress size={18} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                    renderOption={(props, option) => (
                        <li {...props} key={typeof option === 'string' ? option : option.email}>
                            <Box>
                                <Typography variant="body2" fontWeight={600}>{typeof option === 'string' ? option : option.name}</Typography>
                                {typeof option !== 'string' && (
                                    <Typography variant="caption" color="text.secondary">{option.email}</Typography>
                                )}
                            </Box>
                        </li>
                    )}
                />

                {/* Email Chips */}
                {targetEmails.length > 0 ? (
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, maxHeight: 200, overflowY: 'auto', mb: 2 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {targetEmails.map(email => (
                                <Chip
                                    key={email}
                                    label={email}
                                    onDelete={() => handleRemoveEmail(email)}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                    </Paper>
                ) : (
                    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 2, textAlign: 'center' }}>
                        <Typography color="text.secondary" variant="body2">No emails added yet. Search and add users above.</Typography>
                    </Paper>
                )}

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
                    <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={() => setConfirmClear(true)}
                        disabled={targetEmails.length === 0}
                    >
                        Clear All
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={saveLoading ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                        onClick={handleSaveEmails}
                        disabled={saveLoading}
                    >
                        Save Emails
                    </Button>
                </Box>
            </Card>

            {/* Section 2: Interest Rate Configuration */}
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: { xs: 2, sm: 3 }, mb: 4 }}>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>Interest Rate Override</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Override the global interest rate for targeted runs. Leave empty to use the global rate.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <TextField
                        label="Normal Rate (%)"
                        type="number"
                        size="small"
                        value={normalRate}
                        onChange={(e) => setNormalRate(e.target.value)}
                        inputProps={{ min: 0, max: 100, step: 0.01 }}
                        helperText="e.g. 3.5 for 3.5% — leave empty for global rate"
                        sx={{ width: 220 }}
                        placeholder="e.g. 3.5"
                    />
                    <TextField
                        label="Platinum Rate (%)"
                        type="number"
                        size="small"
                        value={platinumRate}
                        onChange={(e) => setPlatinumRate(e.target.value)}
                        inputProps={{ min: 0, max: 100, step: 0.01 }}
                        helperText="e.g. 6 for 6% — leave empty for global rate"
                        sx={{ width: 220 }}
                        placeholder="e.g. 6"
                    />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={rateLoading ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                        onClick={handleSaveRates}
                        disabled={rateLoading}
                    >
                        Save Rates
                    </Button>
                </Box>
            </Card>

            {/* Section 3: Cron Schedules for Targeted Jobs */}
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                <Box sx={{ p: { xs: 2, sm: 3 }, pb: 0 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>Targeted Cron Schedules</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        These cron jobs run independently and process ONLY the emails listed above.
                    </Typography>

                </Box>

                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: { xs: 500, sm: 650 } }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                                <TableCell sx={{ py: { xs: 1.5, sm: 2 } }}><Typography fontWeight={700} variant="body2">Job Name</Typography></TableCell>
                                <TableCell sx={{ py: { xs: 1.5, sm: 2 } }}><Typography fontWeight={700} variant="body2">Schedule</Typography></TableCell>
                                <TableCell sx={{ py: { xs: 1.5, sm: 2 } }}><Typography fontWeight={700} variant="body2">Manual Run</Typography></TableCell>
                                <TableCell align="right" sx={{ py: { xs: 1.5, sm: 2 } }}><Typography fontWeight={700} variant="body2">Actions</Typography></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && targetedJobs.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                                        <CircularProgress size={30} />
                                    </TableCell>
                                </TableRow>
                            ) : targetedJobs.map(jobKey => {
                                const cronExpr = cronSchedules[jobKey] || '0 4 * * *';
                                const setting = cronSettings.find(s => s.jobName === jobKey);
                                const isActive = setting ? setting.isActive : false;
                                const isActionLoading = actionLoading === `toggle-${jobKey}`;

                                return (
                                    <TableRow key={jobKey} hover>
                                        <TableCell sx={{ py: { xs: 1.5, sm: 2 } }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Switch
                                                    size="small"
                                                    checked={isActive}
                                                    onChange={() => handleToggleActive(jobKey, isActive)}
                                                    disabled={isActionLoading}
                                                />
                                                <Typography fontWeight={600} color={isActive ? 'primary' : 'text.disabled'} variant="body2">
                                                    {getJobDisplayName(jobKey)}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ py: { xs: 1.5, sm: 2 } }}>
                                            {editing === jobKey ? (
                                                <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, alignItems: 'center', flexWrap: 'wrap', minWidth: { xs: 250, sm: 'auto' } }}>
                                                    <Typography variant="body2" sx={{ minWidth: { xs: 50, sm: 60 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Daily at</Typography>
                                                    <TextField
                                                        size="small" type="number" value={tempHour}
                                                        onChange={(e) => { const val = parseInt(e.target.value); if (val >= 1 && val <= 12) setTempHour(val); }}
                                                        inputProps={{ min: 1, max: 12, style: { width: '50px' } }}
                                                        label="Hour"
                                                    />
                                                    <Typography variant="body2">:</Typography>
                                                    <TextField
                                                        size="small" type="number" value={tempMinute}
                                                        onChange={(e) => { const val = parseInt(e.target.value); if (val >= 0 && val <= 59) setTempMinute(val); }}
                                                        inputProps={{ min: 0, max: 59, style: { width: '50px' } }}
                                                        label="Min"
                                                    />
                                                    <Box sx={{ display: 'flex', gap: 0.5, ml: { xs: 0, sm: 1 } }}>
                                                        <Button size="small" variant={tempPeriod === 'AM' ? 'contained' : 'outlined'} onClick={() => setTempPeriod('AM')} sx={{ minWidth: 50, py: 0.5 }}>AM</Button>
                                                        <Button size="small" variant={tempPeriod === 'PM' ? 'contained' : 'outlined'} onClick={() => setTempPeriod('PM')} sx={{ minWidth: 50, py: 0.5 }}>PM</Button>
                                                    </Box>
                                                </Box>
                                            ) : (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Paper variant="outlined" sx={{ px: 1.5, py: 0.5, bgcolor: 'primary.50', display: 'inline-block', borderRadius: 2 }}>
                                                        <Typography variant="body2" fontWeight={600} color="primary.dark">
                                                            {cronToReadable(cronExpr)}
                                                        </Typography>
                                                    </Paper>
                                                    <Tooltip title={`Cron: ${cronExpr}`}>
                                                        <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace', cursor: 'help' }}>i</Typography>
                                                    </Tooltip>
                                                </Box>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ py: { xs: 1.5, sm: 2 } }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <TextField type="date" size="small" value={manualDate} onChange={(e) => setManualDate(e.target.value)} sx={{ width: 140 }} InputLabelProps={{ shrink: true }} />
                                                <Button
                                                    variant="contained" size="small" color="secondary"
                                                    startIcon={runLoading === jobKey ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                                                    onClick={() => handleManualRun(jobKey)}
                                                    disabled={runLoading === jobKey || !manualDate || targetEmails.length === 0}
                                                >
                                                    Run
                                                </Button>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            {editing === jobKey ? (
                                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                    <Tooltip title="Save Changes">
                                                        <IconButton color="success" size="small" onClick={() => handleUpdateCronSchedule(jobKey)}>
                                                            <SaveIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Cancel">
                                                        <IconButton color="error" size="small" onClick={() => setEditing(null)}>
                                                            <CancelIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            ) : (
                                                <Button size="small" startIcon={<EditIcon />} variant="outlined"
                                                    onClick={() => {
                                                        setEditing(jobKey);
                                                        const time = parseCronTime(cronExpr);
                                                        let hour12 = time.hour;
                                                        let period: 'AM' | 'PM' = 'AM';
                                                        if (time.hour >= 12) { period = 'PM'; if (time.hour > 12) hour12 = time.hour - 12; }
                                                        else if (time.hour === 0) { hour12 = 12; }
                                                        setTempHour(hour12);
                                                        setTempMinute(time.minute);
                                                        setTempPeriod(period);
                                                    }}
                                                    sx={{ borderRadius: 2 }}
                                                >
                                                    Edit
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>

            {/* Clear Confirmation Dialog */}
            <Dialog open={confirmClear} onClose={() => setConfirmClear(false)}>
                <DialogTitle>Clear All Target Emails?</DialogTitle>
                <DialogContent>
                    <Typography>
                        This will remove all {targetEmails.length} email(s) from the targeted cron list. The scheduled targeted cron jobs will skip processing until new emails are added.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmClear(false)}>Cancel</Button>
                    <Button onClick={handleClearEmails} color="error" variant="contained">Clear All</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TargetedCron;
